export const marketTypes = [
  {
    name: 'Industrial systems',
    detail: 'Buying committees hidden across plants, partners, and technical roles.',
    signal: 'Fragmented route to market',
  },
  {
    name: 'Specialist manufacturing',
    detail: 'Commercial intent appears in hiring, certifications, projects, and upgrades.',
    signal: 'Signals outside the CRM',
  },
  {
    name: 'Technical services',
    detail: 'The right account is easy to describe and difficult to query cleanly.',
    signal: 'Vocabulary-led discovery',
  },
  {
    name: 'Regulated suppliers',
    detail: 'Approved claims, evidence, and timing matter more than sequence volume.',
    signal: 'Proof-sensitive outreach',
  },
  {
    name: 'Distributor ecosystems',
    detail: 'Ownership and opportunity are split between brands, regions, and partners.',
    signal: 'Layered account context',
  },
  {
    name: 'Legacy categories',
    detail: 'The market is real, but the database labels have not kept up.',
    signal: 'Outdated category data',
  },
] as const

export const marketFitSignals = [
  {
    title: 'Expert knowledge defines the market',
    detail:
      'Your team can name the right accounts; ordinary database filters cannot.',
  },
  {
    title: 'Timing lives outside the CRM',
    detail:
      'Hiring, projects, certifications, upgrades, and approvals reveal why now.',
  },
  {
    title: 'Ownership crosses boundaries',
    detail:
      'Plants, regions, partners, and technical roles split the buying picture.',
  },
  {
    title: 'Proof constrains the motion',
    detail:
      'Sources and approved claims matter more than sequence volume.',
  },
] as const

export const marketFitEnvironments = [
  'Industrial systems',
  'Specialist manufacturing',
  'Technical services',
  'Regulated supply chains',
  'Distributor-led markets',
] as const

export const outcomeStandards = [
  {
    id: 'market',
    number: '01',
      kicker: 'Market boundary',
      title: 'Know who belongs.',
      body:
        'Technical language, roles, routes, and evidence define who belongs before outreach begins.',
    visualLabel: 'Replace the category label',
    visualValue: 'A sourced market with explicit exclusions',
    markers: ['Language', 'Roles', 'Routes'],
  },
  {
    id: 'priority',
    number: '02',
      kicker: 'Commercial timing',
      title: 'Catch the moment.',
      body:
        'Hiring, certifications, projects, and upgrades become one commercial reason to act.',
    visualLabel: 'Signals converge',
    visualValue: 'A reason to act now',
    markers: ['Hiring', 'Certification', 'Projects', 'Upgrades'],
  },
  {
    id: 'context',
    number: '03',
      kicker: 'Seller entry',
      title: 'Enter with context.',
      body:
        'Source, buyer role, and why-now reach the seller together before the first call.',
    visualLabel: 'Context travels together',
    visualValue: 'First-call ready',
    markers: ['Original source', 'Buyer role', 'Why now'],
  },
] as const

export type CustomerProof = {
  status: 'approved-public'
  permission: 'named' | 'anonymized'
  customer: string
  market: string
  before: string
  outcome: string
  metric: string
  period: string
  sourceLabel: string
  sourceHref: string
  quote?: {
    body: string
    name: string
    role: string
  }
}

// Customer proof renders only after every required evidence and permission
// field is approved. The outcome section deliberately remains truthful while
// this register is empty.
export const customerProof: readonly CustomerProof[] = []

export const signalPills = [
  'New production line announced',
  'Technical buyer changed roles',
  'Distributor added a territory',
  'Certification window opened',
  'Competitor support pain surfaced',
  'Plant hiring accelerated',
  'Tender language changed',
  'Past champion joined a target',
  'Service hub expansion detected',
  'Specification window may be open',
  'Approved proof matched',
  'Reply needs human context',
] as const

export const capabilityTabs = [
  {
    id: 'reconstruct',
    label: 'Market reconstruction',
    kicker: '01 · Market and signals',
    title: 'Find the market the database misses',
    body:
      'Wave99 combines your commercial truth with company evidence, technical language, role mapping, and live market signals. The result is a reviewable account universe—not a generic export.',
    outcome: 'You receive: a sourced market map with exclusions and confidence notes.',
  },
  {
    id: 'prepare',
    label: 'Outreach production',
    kicker: '02 · Evidence and message',
    title: 'Turn evidence into a reason to talk',
    body:
      'Every account thesis is connected to a source, approved claim, buyer role, and practical next action. Messages are produced inside the boundaries your team sets.',
    outcome: 'You receive: account-specific outreach with the proof attached.',
  },
  {
    id: 'operate',
    label: 'Sending operations',
    kicker: '03 · Safeguards and delivery',
    title: 'Operate the motion without handing you another tool',
    body:
      'Wave99 configures sender health, timing, limits, suppression, channel routing, and reply-stop rules. Your team can inspect the controls without managing them every day.',
    outcome: 'You receive: visible safeguards and an accountable operating owner.',
  },
  {
    id: 'handoff',
    label: 'Reply intelligence',
    kicker: '04 · Triage and handoff',
    title: 'Return the conversation ready to sell',
    body:
      'When a useful reply arrives, automation stops. Wave99 assembles the thread, original evidence, qualification context, owner, and recommended next step.',
    outcome: 'You receive: a qualified handoff, not another unprocessed inbox item.',
  },
] as const

export const personas = [
  {
    label: 'Technical sellers',
    title: 'Walk into the conversation with the “why now” already attached',
    body:
      'Receive accounts that match the real market, with the source evidence, buyer context, thread, and next action in one place.',
    outcomes: [
      ['Less list work', 'Spend time on qualified conversations instead of cleaning exports.'],
      ['More useful context', 'See the technical trigger and approved proof before the first call.'],
    ],
  },
  {
    label: 'Sales leaders',
    title: 'Run a specialist motion without assembling a seven-tool stack',
    body:
      'Wave99 owns the difficult middle between market direction and seller-ready opportunity, while progress and safeguards remain visible.',
    outcomes: [
      ['One accountable motion', 'Research, messaging, sending, replies, and learning share one owner.'],
      ['Clear operating boundary', 'Your team sets direction; Wave99 runs the day-to-day motion.'],
    ],
  },
  {
    label: 'Founders',
    title: 'Test a narrow market before building the whole outbound team',
    body:
      'Translate founder knowledge into a source-backed operating brief, then see which accounts, messages, and signals create real conversations.',
    outcomes: [
      ['Faster market learning', 'Turn replies and objections into reusable market knowledge.'],
      ['No daily campaign burden', 'Stay close to commercial truth without running the machinery.'],
    ],
  },
  {
    label: 'RevOps',
    title: 'Keep the operating controls inspectable and the evidence traceable',
    body:
      'Review the account source, claim boundary, sending rule, reply state, and owner without asking sellers to maintain another workflow.',
    outcomes: [
      ['Visible safeguards', 'Sender limits, suppression, ownership, and audit context stay reviewable.'],
      ['Cleaner handoffs', 'The CRM receives context after qualification instead of noise before it.'],
    ],
  },
  {
    label: 'Marketing',
    title: 'Return live market language to the message, not just the dashboard',
    body:
      'Route objections, terminology, proof gaps, and buyer questions back into the next campaign and the wider positioning system.',
    outcomes: [
      ['Better message evidence', 'See which proof and language created a useful response.'],
      ['A learning loop', 'Keep approved learnings while rejecting unsupported inference.'],
    ],
  },
] as const

export const outputTiles = [
  {
    tone: 'yellow',
    value: '38',
    label: 'reviewable accounts',
    note: 'Representative market map',
  },
  {
    tone: 'green',
    value: '12',
    label: 'account theses',
    note: 'Source linked',
  },
  {
    tone: 'neutral',
    value: 'Source → thesis → thread → owner',
    label: 'The handoff stays explainable',
    note: 'Operating standard',
    wide: true,
  },
  {
    tone: 'neutral',
    value: 'Known / found / interpreted',
    label: 'Facts and inference remain visibly separate',
    note: 'Evidence standard',
    wide: true,
  },
  {
    tone: 'yellow',
    value: '05',
    label: 'launch safeguards',
    note: 'Inspect before send',
  },
  {
    tone: 'pink',
    value: '01',
    label: 'accountable owner',
    note: 'Human escalation',
  },
  {
    tone: 'purple',
    value: '24h',
    label: 'reply triage window',
    note: 'Representative service target',
  },
  {
    tone: 'pink',
    value: '0',
    label: 'invented claims',
    note: 'Non-negotiable',
  },
  {
    tone: 'neutral',
    value: 'A useful reply should arrive with the original reason to talk still attached.',
    label: 'Qualified handoff principle',
    note: 'Wave99 operating method',
    wide: true,
  },
] as const

export const insightCards = [
  {
    category: 'Market reconstruction',
    date: 'Field note · 2026',
    title: 'Why a clean list is the wrong starting point for an outdated niche market',
    tone: 'signal',
  },
  {
    category: 'Operating systems',
    date: 'Method · 2026',
    title: 'The evidence chain every technical outbound handoff should preserve',
    tone: 'route',
  },
  {
    category: 'Human + AI',
    date: 'Playbook · 2026',
    title: 'Where automation should stop and accountable judgment should start',
    tone: 'human',
  },
  {
    category: 'Deliverability',
    date: 'Control note · 2026',
    title: 'What clients should be able to inspect without operating every setting',
    tone: 'control',
  },
] as const

export const operatingStandards = [
  {
    title: 'The source stays attached',
    body: 'An account thesis should be traceable to the evidence that produced it.',
  },
  {
    title: 'Inference is labeled',
    body: 'Known facts, found evidence, and interpretation are never presented as the same thing.',
  },
  {
    title: 'Claims stay inside the brief',
    body: 'Approved proof and exclusions define what the operation is allowed to say.',
  },
  {
    title: 'Reply means stop',
    body: 'Automation pauses when a real response arrives; the thread moves into triage.',
  },
  {
    title: 'Controls remain visible',
    body: 'Sending limits, suppression, health, and ownership can be inspected at any time.',
  },
  {
    title: 'A human owns the edge cases',
    body: 'Ambiguity, risk, and commercial judgment are escalated rather than guessed.',
  },
  {
    title: 'The handoff is complete',
    body: 'Seller, source, reason to talk, reply thread, and recommended next step travel together.',
  },
  {
    title: 'Learning is selective',
    body: 'Only demonstrated facts and approved outcomes are retained for the next motion.',
  },
  {
    title: 'Fit matters more than volume',
    body: 'Narrow markets are protected by exclusions, confidence notes, and deliberate review.',
  },
] as const

export const faqs = [
  {
    question: 'Do we need to operate the Wave99 software?',
    answer:
      'No. Wave99 is delivered as an operated motion. Your team sets the market, offer, proof, exclusions, and commercial priorities; Wave99 runs the research, outreach, sending, reply triage, and learning loop.',
  },
  {
    question: 'What does our team provide at the start?',
    answer:
      'The most useful inputs are market knowledge, offer boundaries, approved proof, exclusions, existing account examples, and the commercial definition of a useful conversation.',
  },
  {
    question: 'Can we inspect or change the operating boundaries?',
    answer:
      'Yes. The portal keeps sources, claims, sender rules, suppression, status, and ownership visible. Strategic changes remain collaborative even though day-to-day operation stays with Wave99.',
  },
  {
    question: 'When does a reply reach our sales team?',
    answer:
      'After automation has stopped and Wave99 has assembled the reply thread, source context, qualification notes, owner, and recommended next step. Exact routing is agreed during onboarding.',
  },
  {
    question: 'Which markets are the strongest fit?',
    answer:
      'Wave99 is strongest where the commercial universe is real but hard to query: industrial systems, specialist manufacturing, technical services, regulated suppliers, distributor networks, and other narrow B2B categories.',
  },
] as const
